import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useLogto } from '@logto/react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { getConfig } from '@/config';
import { useStaffSession, type StaffSession } from '@/providers/useStaffSession';
import { useExchangeGoogleIdToken } from '@/apps/apis/claudeCodeDashboard/hooks';
import {
  AGENT_DASHBOARD_AUTH_CLEARED_EVENT,
  clearAgentDashboardTokens,
  getAgentDashboardToken,
} from '@/apps/apis/claudeCodeDashboard/instance';
import {
  AGENT_DASHBOARD_SESSION_STORAGE_KEY,
  CLAUDE_CODE_SESSION_STORAGE_KEY,
  normalizeDashboardSession,
} from '@/apps/apis/claudeCodeDashboard/session';
import type { DashboardSession } from '@/apps/apis/claudeCodeDashboard/types';

interface Props {
  children: (session: DashboardSession) => ReactNode;
}

const clearStoredSession = () => {
  clearAgentDashboardTokens({ notify: false });
  localStorage.removeItem(AGENT_DASHBOARD_SESSION_STORAGE_KEY);
  localStorage.removeItem(CLAUDE_CODE_SESSION_STORAGE_KEY);
};

const loadSession = (): DashboardSession | null => {
  if (!getAgentDashboardToken()) {
    return null;
  }
  const raw = localStorage.getItem(AGENT_DASHBOARD_SESSION_STORAGE_KEY) ?? localStorage.getItem(CLAUDE_CODE_SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return normalizeDashboardSession(JSON.parse(raw));
  } catch {
    return null;
  }
};

/**
 * The console reads a `DashboardSession`, which the dashboard JWT used to be the only source of.
 * A staff sign-in answers the same two questions — who is this, and are they an admin here — so it
 * is shaped into one rather than teaching every screen a second identity.
 *
 * Being an admin of the agent screens is `ax:manage`, the scope the AX admin role carries.
 */
const staffAsDashboardSession = (staff: StaffSession): DashboardSession | null => {
  // Telemetry is keyed by email; an empty one would match nobody's rows while looking signed in.
  if (staff.status !== 'staff' || !staff.member?.email) {
    return null;
  }
  const { email } = staff.member;
  return {
    employeeId: email,
    email,
    name: staff.member.name,
    picture: null,
    isAdmin: staff.scopes.includes('ax:manage'),
    // The provider owns expiry now; the SDK refreshes the organization token behind the client.
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  };
};

/**
 * Renders the sign-in card and the Google exchange the console shipped with. Nothing here touches
 * the identity provider, so it stays usable on a stage that has no Logto application registered.
 */
const GoogleGate = ({ children, staffSignIn }: Props & { staffSignIn?: ReactNode }) => {
  const [session, setSession] = useState<DashboardSession | null>(() => loadSession());
  const [error, setError] = useState<string | null>(null);
  const exchangeMutation = useExchangeGoogleIdToken((next) => {
    localStorage.setItem(AGENT_DASHBOARD_SESSION_STORAGE_KEY, JSON.stringify(next.session));
    localStorage.removeItem(CLAUDE_CODE_SESSION_STORAGE_KEY);
    setSession(next.session);
  });

  useEffect(() => {
    if (!session) {
      return;
    }

    const syncStoredAuth = () => {
      if (!getAgentDashboardToken() || new Date(session.expiresAt).getTime() < Date.now()) {
        clearStoredSession();
        setSession(null);
      }
    };

    syncStoredAuth();
    window.addEventListener(AGENT_DASHBOARD_AUTH_CLEARED_EVENT, syncStoredAuth);
    window.addEventListener('storage', syncStoredAuth);
    window.addEventListener('focus', syncStoredAuth);

    return () => {
      window.removeEventListener(AGENT_DASHBOARD_AUTH_CLEARED_EVENT, syncStoredAuth);
      window.removeEventListener('storage', syncStoredAuth);
      window.removeEventListener('focus', syncStoredAuth);
    };
  }, [session]);

  const handleSuccess = (response: CredentialResponse) => {
    if (!response.credential) {
      setError('Google did not return a credential. Please try again.');
      return;
    }
    setError(null);
    exchangeMutation.mutate(response.credential, {
      onError: (mutationError) => {
        const message =
          (mutationError as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to exchange Google credential for a dashboard session.';
        setError(message);
      },
    });
  };

  if (session) {
    return <>{children(session)}</>;
  }

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <Card style={{ maxWidth: 420 }} className="shadow-sm">
        <Card.Body className="p-4">
          <h4 className="mb-3">Agent Telemetry Dashboard</h4>
          <p className="text-muted small mb-3">
            Sign in with your <strong>@skin-seoul.com</strong> Google account to view Agent telemetry.
          </p>
          {error && (
            <Alert variant="danger" className="py-2 mb-3">
              {error}
            </Alert>
          )}
          {exchangeMutation.isPending ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Exchanging credential…</span>
            </div>
          ) : (
            <>
              {staffSignIn}
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={() => setError('Google sign-in failed. Please try again.')}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

/**
 * Adds staff sign-in on top of the Google gate. Rendered only where a Logto application exists,
 * because the SDK's hooks throw outside a mounted provider.
 */
const StaffAwareGate = ({ children }: Props) => {
  const staff = useStaffSession();
  const { signIn } = useLogto();

  // Staff sign-in wins: it is the credential the console is moving to, and the browser may still
  // be holding a dashboard JWT from before.
  const staffSession = staffAsDashboardSession(staff);
  if (staffSession) {
    return <>{children(staffSession)}</>;
  }

  if (staff.status === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center gap-2" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" size="sm" />
        <span>Checking your access…</span>
      </div>
    );
  }

  // A refusal explains itself inside the Google gate rather than replacing it: the account may be
  // the wrong one, and a browser still holding a dashboard session should not lose it over that.
  if (staff.status === 'refused') {
    return (
      <GoogleGate
        staffSignIn={
          <Alert variant="warning" className="py-2 mb-3">
            You are signed in, but not a member of the staff organization. Ask an administrator to add you.
          </Alert>
        }
      >
        {children}
      </GoogleGate>
    );
  }

  return (
    <GoogleGate
      staffSignIn={
        <Button
          variant="primary"
          className="w-100 mb-3"
          onClick={() => void signIn(`${window.location.origin}/callback`)}
        >
          Sign in with your SkinSeoul account
        </Button>
      }
    >
      {children}
    </GoogleGate>
  );
};

const ClaudeCodeLoginGate = ({ children }: Props) =>
  getConfig().logtoAppId ? <StaffAwareGate>{children}</StaffAwareGate> : <GoogleGate>{children}</GoogleGate>;

export default ClaudeCodeLoginGate;
